/* eslint-disable no-unused-vars */
/* eslint-disable consistent-return */
import React, { useEffect, useRef } from 'react';
import Slider from '@mui/material/Slider';
import IconButton from '@mui/material/IconButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useSelector, useDispatch } from 'react-redux';
import { GuiStyles } from './guiStyles';
import slices from '../../state/slices/data';

const toNumber = (v, fb) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fb;
};

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

export default function RangeInput({
  min,
  max,
  value,
  animationSpeed,
  onChange,
  formatLabel,
}) {
  const frameRef = useRef(null);
  const lastTickRef = useRef(0);
  const dispatch = useDispatch();

  const { setAnimating, toggleAnimating } = slices.actions;
  const isAnimating = useSelector((s) => s.data.isAnimating);

  const [raw0, raw1] =
    Array.isArray(value) && value.length === 2 ? value : [min, max];

  const v0 = clamp(toNumber(raw0, min), min, max);
  const v1 = clamp(toNumber(raw1, max), min, max);

  // Fixed window size in days
  const fixedSpan = Math.max(0, v1 - v0);

  // Adjust to taste
  const stepMs = 250;

  useEffect(() => {
    if (!isAnimating) {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      return;
    }

    const tick = (ts) => {
      if (!lastTickRef.current) lastTickRef.current = ts;

      const elapsed = ts - lastTickRef.current;

      if (elapsed >= stepMs) {
        let nextStart = v0 + 1;
        let nextEnd = v1 + 1;

        if (nextEnd > max) {
          nextStart = min;
          nextEnd = min + fixedSpan;
        }

        onChange([nextStart, nextEnd]);
        lastTickRef.current = ts;
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      lastTickRef.current = 0;
    };
  }, [isAnimating, v0, v1, fixedSpan, min, max, onChange]);

  const handlePlayPause = () => dispatch(toggleAnimating());

  return (
    <GuiStyles>
      <div className="innerGUI">
        <div className="icon-button">
          <IconButton
            color="primary"
            disabled={min === max}
            onClick={handlePlayPause}
            title={isAnimating ? 'Pause' : 'Animate'}
          >
            {isAnimating ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </div>

        <div className="slider-wrap">
          <Slider
            min={min}
            max={max}
            step={1}
            value={[v0, v1]}
            disableSwap
            onChange={(_, newValue, activeThumb) => {
              dispatch(setAnimating(false));

              const arr = Array.isArray(newValue) ? newValue : [v0, v1];
              const next0 = clamp(toNumber(arr[0], v0), min, max);
              const next1 = clamp(toNumber(arr[1], v1), min, max);

              let nextRange;

              if (activeThumb === 0) {
                // Left thumb moved: preserve span by moving right thumb too
                let start = next0;
                let end = start + fixedSpan;

                if (end > max) {
                  end = max;
                  start = Math.max(min, end - fixedSpan);
                }

                nextRange = [start, end];
              } else {
                // Right thumb moved: preserve span by moving left thumb too
                let end = next1;
                let start = end - fixedSpan;

                if (start < min) {
                  start = min;
                  end = Math.min(max, start + fixedSpan);
                }

                nextRange = [start, end];
              }

              onChange(nextRange);
            }}
            valueLabelDisplay="auto"
            valueLabelFormat={formatLabel}
          />
        </div>
      </div>
      <div className="slider-dates">
        <span>{formatLabel(min)}</span>
        <span>{formatLabel(max)}</span>
      </div>
    </GuiStyles>
  );
}
